import { getPromiseHandler } from '@tencent/wphk-base-utils';
import { reactive, ref } from '@vue/composition-api';
import { url } from '@tencent/ppd-base-libs';
import { useCouponStore } from '@/store/coupon';
import { storeToRefs } from 'pinia';
// import { ensureChannelCouponId } from '@/utils/channel-coupon';

/**
 * 详情弹窗类型
 */
export enum DetailDialogType {
  'coupon' = 'coupon',
  'task' = 'task',
}

/**
 * 展示数据
 */
let showData = reactive({
  /** 是否展示券 or 任务弹窗 */
  isShow: false,
  /** 展示的类型 */
  showType: DetailDialogType.coupon,
  /** 券 id / 任务 id */
  id: '',
});
/** 券详情所属的 tab */
const couponDetailTab = ref<string>('');

/** 弹窗句柄 */
const promiseHandler = getPromiseHandler();

export const useCouponTaskDialog = () => {
  /**
   * 通过 url 获取 券 id 或者任务 id 信息
   */
  const showDialogByUrl = async () => {
    const urlIdData = getIdFromUrl();
    if (!urlIdData) return;
    showDialog(urlIdData.id, urlIdData.showType);
  };
  /**
   * 通过点击 banner 获取券 id 或者任务 id 信息
   */
  const showDialogByBanner = async (id: string, showType: DetailDialogType) => {
    if (!id || !showType) return;
    showDialog(id, showType);
  };
  /**
   * 打开弹窗之前
   */
  const beforeShowDialog = (id, showType) => {
    if (showType === DetailDialogType.coupon) {
      const couponStore = useCouponStore();
      const { formattedCouponList, couponStateEndList } = storeToRefs(couponStore);
      const coupon = [
        ...formattedCouponList.value,
        ...couponStateEndList.value,
      ].find((coupon) => coupon.couponId === id);
      couponDetailTab.value = coupon?.tabKey?.[0] || '';
    }
  };
  /**
   * 打开券 or 任务详情弹窗
   */
  const showDialog = async (id: string, showType: DetailDialogType) => {
    beforeShowDialog(id, showType);
    showData = Object.assign(showData, { isShow: true, id, showType });
    return promiseHandler.promise;
  };
  /**
   * 关闭券 or 任务详情弹窗
   */
  const hideDialog = async () => {
    showData = Object.assign(showData, {
      isShow: false,
      id: '',
    });
    couponDetailTab.value = '';
    promiseHandler.resolve(true);
  };
  /**
   * 是否展示任务详情弹窗
   * @param taskId 任务 id
   * @returns
   */
  const isShowTaskDetail = (cb) => {
    if (showData.showType === DetailDialogType.task && showData.isShow) {
      cb(showData.id);
    };
  };

  /**
   * 是否展示券详情弹窗
   * @param couponId 券 id
   * @returns
   */
  const isShowCouponDetail = (cb) => {
    if (showData.showType === DetailDialogType.coupon && showData.isShow) {
      cb(showData.id);
    }
  };

  return {
    /** 展示数据 */
    showData,
    /** 通过 url 获取 券 id 或者任务 id 信息 */
    showDialogByUrl,
    /** 通过点击 banner 获取券 id 或者任务 id 信息 */
    showDialogByBanner,
    /** 关闭券 or 任务详情弹窗 */
    hideDialog,
    /** 是否展示任务详情弹窗 */
    isShowTaskDetail,
    /** 是否展示券详情弹窗 */
    isShowCouponDetail,
    /** 券详情弹窗当前所属的 tab */
    couponDetailTab,
  };
};

/**
 * 从 url 中获取id 信息，例如
 * couponId=CP2587202302228095,
 * taskId=B2C_BASIC_2303
 * 只展示其中一种
 */
function getIdFromUrl(): { id: string; showType: DetailDialogType } | null {
  const taskId = url.getUrlParam('taskId') || '';
  if (taskId) {
    return { id: taskId, showType: DetailDialogType.task };
  }

  const couponId = url.getUrlParam('couponId') || '';
  if (couponId) {
    return { id: couponId, showType: DetailDialogType.coupon };
  }

  return null;
}
